/* eslint @typescript-eslint/naming-convention:0 */
import { useEffect, useState } from 'react'
import { Alert, AlertColor, AlertTitle, Box, Button, Snackbar, Step, StepLabel, Stepper, Typography, useMediaQuery } from '@mui/material'
import { isEmpty, isNil, omit } from 'lodash/fp'
import log from 'loglevel'
import { type FormInputType } from '../../functions/types'
import { useFormContext, useFormState } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormInputSchema } from '../../functions/schema'
import FormInput from '../FormInput'
import alertPropaties from './alert-propaties.json'
import { ThanksPage } from '../ThanksPage'
import FormConfirm from '../FormConfirm'
import {
  Form,
  useCreate,
  useGetOne,
  useRedirect,
  useTranslate
} from 'react-admin'
import { format } from 'date-fns/fp'
import { v4 } from 'uuid'
import { TerashiteHeader } from '../Header'
import { TerashiteFooter } from '../Footer'
log.setLevel(log.levels.DEBUG)
const buttonNames = {
  next: "resources.buttons.next",
  back: "resources.buttons.back",
  submit: "resources.buttons.submit"
}
enum ActiveSteps {
  form = 0,
  confirm = 1,
  complete = 2
}
const NextButton: React.FC<{
  activeStep: ActiveSteps,
  schema: any,
  setActiveStep: (value: React.SetStateAction<number>) => void
}> = ({ activeStep, schema, setActiveStep }) => {
  const [enable, setEnable] = useState(false)
  const { getValues } = useFormContext()
  const { isValid } = useFormState()
  const translate = useTranslate()
  const redirect = useRedirect()
  const [create] = useCreate<{ projectId: string, data: { id: string, sendDate: string, formData: FormInputType } }, Error>(undefined, undefined, {
    onError: (error: Error) => {
      log.error('[OrderFormR.SubmitButton.create]', { error })
    },
    onSettled: (
      data: { projectId: string, data: { id: string, sendDate: string, formData: FormInputType } } | undefined,
      error: Error | null
    ) => {
      if (!isNil(error) || isNil(data)) {
        log.error({ error, data })
      }
    }
  })
  const onClick = (() => {
    if (activeStep === ActiveSteps.confirm) {
      const data = getValues() as FormInputType
      const sendDate = format('yyyy-MM-dd HH:mm:ss')(new Date())
      create("requests", { data: { projectId: "019e719d-361f-7331-bbf1-d4956cbc484b", data: { id: v4(), sendDate, formData: data } } })
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  })
  const data = getValues()
  const parsed = schema.safeParse(data)
  useEffect(() => {
    setEnable(parsed.success)
  }, [isValid, parsed])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px', "&.MuiButton-root.Mui-disabled": { backgroundColor: '#ccc' }, "&.MuiButton-root": { backgroundColor: '#f57800' } }}
      disabled={!enable}
      fullWidth
      onClick={onClick}>
      <Typography variant="button">
        {translate(buttonNames[activeStep === ActiveSteps.confirm ? "submit" : "next"])}
      </Typography>
    </Button>
  )
}
const BackButton: React.FC<{
  activeStep: ActiveSteps,
  handleBack: () => void
}> = ({ activeStep, handleBack }) => {
  const translate = useTranslate()
  return (
    <Button variant="outlined"
      disabled={activeStep === 0}
      onClick={handleBack}

      sx={{ borderRadius: '10px', "&.MuiButton-root.Mui-disabled": { borderColor: '#ccc', color: '#ccc' }, "&.MuiButton-root": { borderColor: '#f57800', color: '#f57800' } }}>
      {translate(buttonNames.back)}
    </Button>)
}
export const FormBase: React.FC<{
  projectId: string
}> = ({ projectId }) => {
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const configs = omit(['id'])(useGetOne('configs', { id: projectId }).data)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [alertStatus, setAlertStatus] = useState<AlertColor | undefined>(undefined)
  const contentArray = [
    <FormInput configs={configs} />,
    <FormConfirm configs={configs} />
  ]
  const contentNameArray = [
    "入力画面",
    "ご確認画面",
    "完了"
  ]

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }
  const handleAlertClose = () => {
    setAlertStatus(undefined)
  }
  const getStepContent = (
    stepIndex: number,
    contentArray: Array<JSX.Element>
  ): JSX.Element | undefined => {
    return stepIndex >= contentArray.length ? undefined : contentArray[stepIndex]
  }
  return (
    <Box sx={{ marginTop: isDesktop ? '10em' : '5em' }}>
      <TerashiteHeader />
      <Form
        resolver={zodResolver(FormInputSchema)}
        mode="onChange"
        reValidateMode="onChange"
      // defaultValues={defaultValues}
      >
        <Box sx={{ mx: isDesktop ? '20em' : '1em', paddingBottom: '2em' }} >
          <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ '.MuiStepIcon-root.Mui-active': { color: "#f57800" }, '.MuiStepIcon-root.Mui-completed': { color: "#f57800" } }}>
              {contentNameArray.map((name) => (
                <Step key={name}>
                  <StepLabel>{name}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {activeStep === contentArray.length ? (
              <Box>
                {<ThanksPage />}
              </Box>
            ) : (
              <Box>
                {getStepContent(activeStep, contentArray)}
                <Box display={"flex"} justifyContent={"space-around"} paddingTop={3}>
                  <Box justifyContent={"start"}>
                    <BackButton activeStep={activeStep} handleBack={handleBack} />
                  </Box>
                  <Box justifyContent={"end"}>
                    <NextButton activeStep={activeStep} schema={FormInputSchema} setActiveStep={setActiveStep} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <Snackbar open={!isEmpty(alertStatus)} autoHideDuration={5000} onClose={handleAlertClose}>
            <Alert severity={alertStatus}>
              <AlertTitle>{!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertTitle"] : ""}</AlertTitle>
              {!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertMessage"] : ""}
            </Alert>
          </Snackbar>
        </Box>
      </Form>
      <TerashiteFooter />
    </Box>
  )
}
export default FormBase